import React from "react";
import { Box, Button, IconButton, Tooltip, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import { useNavigate } from "react-router-dom";
import { writeHandoff } from "../../pages/DocumentGenerator/studioStore";

/**
 * Launcher for Rego AI document editing.
 *
 * Navigates to the Document Studio in the same tab. Pass `onClick` to override.
 */
const GENERATOR_PATH = "/document-generator";

const openStudio = (file, navigate) => {
  const fileId =
    file?.fileId != null && String(file.fileId).length > 0
      ? String(file.fileId)
      : "scratch";

  const returnTo =
    file?.returnTo ||
    `${window.location.pathname}${window.location.search}`;

  if (file) writeHandoff({ ...file, fileId, returnTo, launchedAt: Date.now() });

  navigate(
    `${GENERATOR_PATH}?fileId=${encodeURIComponent(fileId)}&returnTo=${encodeURIComponent(returnTo)}`
  );
};

const getStyles = (theme) => {
  const tc = theme.customColors;
  const info = tc.status.info;

  return {
    tc,
    info,
    iconBadge: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 26,
      height: 26,
      borderRadius: "8px",
      flexShrink: 0,
      background: `linear-gradient(135deg, ${info.main} 0%, ${tc.button.primary} 100%)`,
      boxShadow: `0 2px 8px ${alpha(info.main, 0.35)}`,
      animation: "regoAiIconPulse 2.8s ease-in-out infinite",
      "@keyframes regoAiIconPulse": {
        "0%, 100%": {
          transform: "scale(1)",
          boxShadow: `0 2px 8px ${alpha(info.main, 0.35)}`,
        },
        "50%": {
          transform: "scale(1.04)",
          boxShadow: `0 4px 14px ${alpha(info.main, 0.45)}`,
        },
      },
    },
    iconButton: {
      width: 40,
      height: 40,
      borderRadius: "12px",
      border: `1px solid ${alpha(info.main, 0.22)}`,
      bgcolor: tc.background.info,
      color: info.main,
      boxShadow: tc.shadow.card,
      transition: "all 0.2s ease",
      "&:hover": {
        bgcolor: tc.status.info.bg,
        borderColor: info.main,
        transform: "translateY(-1px)",
        boxShadow: tc.shadow.filterChip,
      },
    },
    outlineButton: {
      border: `1px solid ${alpha(info.main, 0.28)}`,
      bgcolor: tc.background.info,
      color: tc.text.strong,
      boxShadow: tc.shadow.card,
      "&:hover": {
        borderColor: info.main,
        bgcolor: tc.status.info.bg,
        boxShadow: tc.shadow.filterChip,
        transform: "translateY(-1px)",
      },
    },
    solidButton: {
      bgcolor: tc.button.primary,
      color: tc.button.text,
      border: "none",
      boxShadow: `0 4px 14px ${alpha(tc.button.primary, 0.35)}`,
      "&:hover": {
        bgcolor: tc.button.hover,
        boxShadow: `0 6px 18px ${alpha(tc.button.primary, 0.42)}`,
        transform: "translateY(-1px)",
      },
    },
    shimmerText: {
      background: `linear-gradient(90deg, ${tc.text.strong} 40%, ${info.main} 50%, ${tc.text.strong} 60%)`,
      backgroundSize: "200% auto",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      animation: "regoAiLabelShimmer 3.5s linear infinite",
      "@keyframes regoAiLabelShimmer": {
        "0%": { backgroundPosition: "0% center" },
        "100%": { backgroundPosition: "200% center" },
      },
    },
  };
};

const renderLabel = (label, shimmerTextSx) => {
  const parts = String(label).split(/(Rego AI)/);
  if (parts.length === 1) return label;

  return parts.map((part, i) =>
    part === "Rego AI" ? (
      <Box component="span" key={i} sx={shimmerTextSx}>
        Rego AI
      </Box>
    ) : (
      <Box component="span" key={i}>
        {part}
      </Box>
    )
  );
};

const GenerateDocButton = ({
  file,
  label = "Edit with AI",
  variant = "outline",
  size = "medium",
  onClick,
  sx = {},
  ...rest
}) => {
  const theme = useTheme();
  const styles = getStyles(theme);
  const navigate = useNavigate();

  const handleClick = (e) => {
    e?.stopPropagation?.();
    if (onClick) {
      onClick(e);
      return;
    }
    openStudio(file, navigate);
  };

  const aiIcon = (
    <Box sx={styles.iconBadge}>
      <TipsAndUpdatesIcon sx={{ fontSize: 15, color: styles.tc.button.text }} />
    </Box>
  );

  if (variant === "icon") {
    return (
      <Tooltip title={label}>
        <IconButton
          onClick={handleClick}
          size={size}
          sx={{ ...styles.iconButton, ...sx }}
          {...rest}
        >
          {aiIcon}
        </IconButton>
      </Tooltip>
    );
  }

  const isSolid = variant === "solid";

  return (
    <Button
      onClick={handleClick}
      size={size}
      variant={isSolid ? "contained" : "outlined"}
      color="primary"
      disableElevation
      startIcon={aiIcon}
      sx={{
        textTransform: "none",
        fontWeight: 600,
        fontSize: theme.typography.body2.fontSize,
        fontFamily: theme.typography.fontFamily,
        letterSpacing: "-0.01em",
        borderRadius: "999px",
        px: 2,
        py: 0.85,
        minHeight: 40,
        gap: 0.75,
        whiteSpace: "nowrap",
        transition: "all 0.2s ease",
        "& .MuiButton-startIcon": {
          mr: 0.75,
          ml: 0,
        },
        ...(isSolid ? styles.solidButton : styles.outlineButton),
        ...sx,
      }}
      {...rest}
    >
      {renderLabel(label, styles.shimmerText)}
    </Button>
  );
};

export default GenerateDocButton;
